/**
 * landing.js - icon hydration, the time-of-day theme, and the card's rotating greeting.
 *
 * THEME IS AUTOMATIC (owner decision, 2026-10-01, replacing the manual three-theme
 * switch): Light clean from 06:00 to 18:00 by the VISITOR'S clock, Soft glow
 * (dark) otherwise. The same rule runs as an inline script in <head> so the first
 * paint is already right; the copy here exists so a page left open across 18:00
 * or 06:00 follows the day. Keep the two hour constants in step.
 */

import { hydrateIcons } from '../icons.js';

const DAY_STARTS = 6;
const NIGHT_STARTS = 18;

function themeFor(date) {
    const h = date.getHours();
    return h >= DAY_STARTS && h < NIGHT_STARTS ? 'light-clean' : 'dark-premium';
}

function applyTheme() {
    document.documentElement.dataset.theme = themeFor(new Date());
}

/**
 * The greeting in the dashboard preview card, in South African languages: the
 * most recognisable of the eleven. {name} is a sample student's name, rotating
 * through the owner's list; the language is NOT shown beside it (owner,
 * 2026-10-01). English follows the hour.
 */
const SAMPLE_NAMES = ['Siphesihle', 'Emmanuel', 'Mokhothu', 'Xorana', 'Ofentse', 'Omphile', 'Lebohang', 'Ntate Moloi'];
// The names step by 3 while the greetings step by 1. Both lists have 8 entries, so
// stepping both by 1 would pair each greeting with one name forever; a stride that
// shares no factor with 8 shows every name with a different greeting each cycle.
const NAME_STRIDE = 3;

function englishGreeting(date) {
    const h = date.getHours();
    return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

const GREETINGS = [
    { text: 'Sawubona, {name}', lang: 'zu' },               // isiZulu
    { text: 'Molo, {name}', lang: 'xh' },                    // isiXhosa
    { text: 'Lumela, {name}', lang: 'st' },                  // Sesotho
    { text: 'Dumela, {name}', lang: 'tn' },                  // Setswana
    { text: 'Thobela, {name}', lang: 'nso' },                // Sepedi
    { text: 'He ndaa, {name}! Vha ri mini?', lang: 've' },   // Tshivenda
    { text: 'Hallo, {name}', lang: 'af' },                   // Afrikaans
    { text: '{english}, {name}', lang: 'en' },               // English
];
const GREETING_MS = 3200;

function greetingText(g, name) {
    return g.text.replace('{english}', englishGreeting(new Date())).replace('{name}', name);
}

function startGreetings() {
    const box = document.querySelector('[data-greeting]');
    const word = box && box.querySelector('[data-greeting-word]');
    if (!word) return;

    // Someone who has asked for less motion gets one fixed greeting, not a loop.
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduce.matches) return;

    let i = 0;
    let n = 0;   // the markup already shows greeting 0 with name 0
    setInterval(() => {
        box.setAttribute('data-greeting-out', '');
        // Swap while it is faded out, then fade back in.
        setTimeout(() => {
            i = (i + 1) % GREETINGS.length;
            n = (n + NAME_STRIDE) % SAMPLE_NAMES.length;
            // Each time the greetings wrap, nudge the names one extra place, so the
            // pairings keep changing for 64 steps instead of repeating every 8.
            if (i === 0) n = (n + 1) % SAMPLE_NAMES.length;
            word.textContent = greetingText(GREETINGS[i], SAMPLE_NAMES[n]);
            word.setAttribute('lang', GREETINGS[i].lang);
            box.removeAttribute('data-greeting-out');
        }, 300);
    }, GREETING_MS);
}

hydrateIcons(document);
applyTheme();
setInterval(applyTheme, 60 * 1000);
startGreetings();
